import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton, Breadcrumbs, Link as MuiLink, Tooltip } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Home as HomeIcon } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';

/**
 * Cabeçalho de página reutilizável com botão de voltar e breadcrumbs opcionais.
 *
 * Props:
 *  title       — título principal da página (obrigatório)
 *  subtitle    — subtítulo opcional
 *  backTo      — rota exacta para voltar; se omitido usa navigate(-1)
 *  backLabel   — texto do tooltip do botão de voltar (default: "Voltar")
 *  breadcrumbs — array de { label, to } para breadcrumb; o último item é o título corrente
 *  actions     — nó React para botões de acção à direita
 */
const PageHeader = ({ title, subtitle, backTo, backLabel = 'Voltar', breadcrumbs, actions }) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (backTo) navigate(backTo);
    else navigate(-1);
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs sx={{ mb: 1, fontSize: '0.8rem' }}>
          <MuiLink
            component={Link}
            to="/"
            underline="hover"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <HomeIcon sx={{ fontSize: 14 }} />
            Início
          </MuiLink>
          {breadcrumbs.map((crumb) =>
            crumb.to ? (
              <MuiLink
                key={crumb.label}
                component={Link}
                to={crumb.to}
                underline="hover"
                color="inherit"
              >
                {crumb.label}
              </MuiLink>
            ) : (
              <Typography key={crumb.label} color="text.primary" sx={{ fontSize: '0.8rem' }}>
                {crumb.label}
              </Typography>
            )
          )}
        </Breadcrumbs>
      )}

      {/* Título + botão voltar + acções */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={backLabel}>
            <IconButton
              onClick={handleBack}
              size="small"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                mt: 0.3,
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box>
            <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {actions && (
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {actions}
          </Box>
        )}
      </Box>
    </Box>
  );
};

PageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  backTo: PropTypes.string,
  backLabel: PropTypes.string,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, to: PropTypes.string })
  ),
  actions: PropTypes.node,
};

export default PageHeader;
